import { Plus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Modal } from './ui'
import { useToast } from '../contexts/ToastContext'
import AdminUserStatusDot from './admin/AdminUserStatusDot'
import {
  createAdminOrganization,
  createAdminUser,
  fetchAdminOrganizations,
  fetchAdminUsers,
  type AdminOrganization,
  type AdminUser,
} from '../services/admin'
import { formatDateTimeDa } from '../utils/format'
import { validateEmail } from '../utils/emailValidation'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'
const labelClass =
  'block text-xs uppercase tracking-wide text-ink-500 font-medium mb-0.5'
const actionButtonClass =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-ink border border-rule rounded-sm bg-paper hover:bg-paper-200/80 transition-colors'
const truncateCellClass = 'min-w-0 truncate text-xs text-ink'
/** Dot | Navn | E-mail | Organisation | Seneste aktivitet (fast bredde til timestamp) */
const userRowGrid =
  'grid grid-cols-[1.5rem_minmax(0,1.15fr)_minmax(0,1.15fr)_minmax(0,0.7fr)_8.5rem] gap-x-3 items-center px-3 py-2'

const AdminUsersPanel: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newOrgId, setNewOrgId] = useState<number | ''>('')
  const [createBusy, setCreateBusy] = useState(false)

  const [showNewOrg, setShowNewOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [orgBusy, setOrgBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [list, orgs] = await Promise.all([fetchAdminUsers(), fetchAdminOrganizations()])
      setUsers(list)
      setOrganizations(orgs)
      setNewOrgId((prev) => (prev === '' && orgs[0] ? orgs[0].id : prev))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente brugere')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const check = validateEmail(newEmail)
    if (!check.ok) {
      showToast(check.message)
      return
    }
    if (!newName.trim()) {
      showToast('Navn er påkrævet.')
      return
    }
    if (newOrgId === '') {
      showToast('Vælg en organisation.')
      return
    }
    setCreateBusy(true)
    try {
      const { user: created, mailDelivery } = await createAdminUser(
        check.email,
        newName.trim(),
        newOrgId
      )
      setUsers((prev) =>
        [...prev.filter((u) => u.id !== created.id), created].sort((a, b) =>
          a.name.localeCompare(b.name, 'da')
        )
      )
      setShowCreate(false)
      setNewEmail('')
      setNewName('')
      setNewOrgId(organizations[0]?.id ?? '')
      const mailNote =
        mailDelivery === 'log'
          ? ' Invitation logget (dev).'
          : ' Invitation sendt på e-mail.'
      showToast(`Bruger oprettet.${mailNote}`)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Opret fejlede')
    } finally {
      setCreateBusy(false)
    }
  }

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault()
    const name = newOrgName.trim()
    if (!name) return
    setOrgBusy(true)
    try {
      const created = await createAdminOrganization(name)
      setOrganizations((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'da'))
      )
      setNewOrgId(created.id)
      setNewOrgName('')
      setShowNewOrg(false)
      showToast('Organisation oprettet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Opret fejlede')
    } finally {
      setOrgBusy(false)
    }
  }

  function openUser(userId: number) {
    navigate(`/account/admin/brugere/${userId}`)
  }

  return (
    <section className={embedded ? undefined : 'mt-10 pt-8 border-t border-rule'}>
      {!embedded && (
        <>
          <p className="font-mono text-xs uppercase tracking-widest text-signal mb-1.5">
            Administration
          </p>
          <h2 className="font-serif text-xl text-ink mb-3">Brugerstyring</h2>
        </>
      )}

      {loading && <p className="text-sm text-ink-500">Henter brugere…</p>}
      {error && (
        <p className="text-sm text-signal mb-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            setNewEmail('')
            setNewName('')
            setNewOrgId(organizations[0]?.id ?? '')
            setShowCreate(true)
          }}
          className={actionButtonClass}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Opret bruger
        </button>
        <button
          type="button"
          onClick={() => {
            setNewOrgName('')
            setShowNewOrg(true)
          }}
          className={actionButtonClass}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Ny organisation
        </button>
      </div>

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Opret bruger"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 text-sm text-ink-500 hover:text-ink"
            >
              Annuller
            </button>
            <button
              type="submit"
              form="admin-create-user-form"
              disabled={createBusy}
              className="px-3 py-1.5 bg-ink text-paper text-sm rounded-sm disabled:opacity-50"
            >
              {createBusy ? 'Opretter…' : 'Opret og send invitation'}
            </button>
          </>
        }
      >
        <form id="admin-create-user-form" onSubmit={handleCreate} className="space-y-2.5">
          <div>
            <label htmlFor="admin-new-email" className={labelClass}>
              E-mail
            </label>
            <input
              id="admin-new-email"
              type="email"
              className={inputClass}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="admin-new-name" className={labelClass}>
              Visningsnavn
            </label>
            <input
              id="admin-new-name"
              type="text"
              className={inputClass}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="admin-new-org" className={labelClass}>
              Organisation
            </label>
            <select
              id="admin-new-org"
              className={inputClass}
              value={newOrgId === '' ? '' : String(newOrgId)}
              onChange={(e) =>
                setNewOrgId(e.target.value === '' ? '' : Number(e.target.value))
              }
              required
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

      <Modal
        open={showNewOrg}
        onClose={() => {
          setShowNewOrg(false)
          setNewOrgName('')
        }}
        title="Ny organisation"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setShowNewOrg(false)
                setNewOrgName('')
              }}
              className="px-3 py-1.5 text-sm text-ink-500 hover:text-ink"
            >
              Annuller
            </button>
            <button
              type="submit"
              form="admin-create-org-form"
              disabled={orgBusy || !newOrgName.trim()}
              className="px-3 py-1.5 bg-ink text-paper text-sm rounded-sm disabled:opacity-50"
            >
              {orgBusy ? 'Opretter…' : 'Opret'}
            </button>
          </>
        }
      >
        <form id="admin-create-org-form" onSubmit={handleCreateOrg}>
          <label htmlFor="admin-new-org-name" className={labelClass}>
            Navn
          </label>
          <input
            id="admin-new-org-name"
            type="text"
            className={inputClass}
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="Organisationsnavn"
            autoFocus
            required
          />
        </form>
      </Modal>

      {!loading && users.length > 0 && (
        <div className="overflow-x-auto border border-rule rounded-sm">
          <div role="table" className="w-full min-w-[40rem] text-xs text-left">
            <div role="rowgroup">
              <div
                role="row"
                className={`${userRowGrid} border-b border-rule bg-paper-200/50 text-xs uppercase tracking-wide text-ink-500`}
              >
                <span role="columnheader" aria-hidden />
                <span role="columnheader" className="font-medium">
                  Navn
                </span>
                <span role="columnheader" className="font-medium">
                  E-mail
                </span>
                <span role="columnheader" className="font-medium min-w-0 truncate">
                  Organisation
                </span>
                <span
                  role="columnheader"
                  className="font-medium text-right whitespace-nowrap"
                >
                  Seneste aktivitet
                </span>
              </div>
            </div>
            <div role="rowgroup" className="divide-y divide-rule">
              {users.map((u) => (
                <div
                  key={u.id}
                  role="row"
                  onClick={() => openUser(u.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openUser(u.id)
                    }
                  }}
                  tabIndex={0}
                  aria-label={`Åbn profil for ${u.name}`}
                  className={`${userRowGrid} cursor-pointer hover:bg-paper-200/40 transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-400`}
                >
                  <span role="cell" className="flex justify-center">
                    <AdminUserStatusDot user={u} />
                  </span>
                  <span role="cell" className={truncateCellClass} title={u.name}>
                    {u.name}
                    {u.isAdmin && (
                      <span className="ml-1.5 text-[10px] uppercase text-signal">admin</span>
                    )}
                  </span>
                  <span role="cell" className={truncateCellClass} title={u.email}>
                    {u.email}
                  </span>
                  <span
                    role="cell"
                    className={truncateCellClass}
                    title={u.organization.name}
                  >
                    {u.organization.name}
                  </span>
                  <span
                    role="cell"
                    className="text-right text-ink-500 tabular-nums whitespace-nowrap"
                    title={formatDateTimeDa(u.lastActivityAt) ?? undefined}
                  >
                    {formatDateTimeDa(u.lastActivityAt) ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default AdminUsersPanel
